import { Badge, Flex, IconButton, Text } from '@radix-ui/themes';
import React, { useState, useEffect, useCallback } from 'react';
import hospitalLogo from '../../assets/hospital-medical-svgrepo-com.svg';
import { FaRegBell, FaRegUser } from 'react-icons/fa';
import { FaPowerOff } from 'react-icons/fa6';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../config/axiosConfig';
import NotificationDropdown from '../ui-components/NotificationDropdown';
import ProfilePopup from '../ui-components/ProfilePopup';
import ConfirmationDialogue from '../ui-components/ConfirmationDialogue';

const SHOW_DROPDOWN_GAP = true;

const ROLE_INDICATORS = {
  '1': '👑',
  '2': '⚙️',
  '3': '🏢',
  '4': '❤️‍🩺',
  '5': '🛡️',
  '6': '🧹',
  '7': '🍽️',
};

const Header = () => {
  const { authenticated, institutionName, department, logout } = useAuth();
  const roleId = localStorage.getItem('roleId');
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/Notification/get-last10', {
        params: { _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });
      if (response.data && response.data.result) {
        setNotifications(response.data.result);
        setHasUnread(response.data.result.some((n) => !n.isRead));
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const updateReadStatus = useCallback(async () => {
    try {
      await axiosInstance.post('/Notification/update-read-status');
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true }))
      );
      setHasUnread(false);
    } catch (error) {
      console.error('Error updating read status:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const handleRefreshNotifications = () => {
      console.log('Header received refresh notifications event');
      fetchNotifications();
    };
    window.addEventListener('refreshNotifications', handleRefreshNotifications);
    return () => {
      window.removeEventListener('refreshNotifications', handleRefreshNotifications);
    };
  }, [fetchNotifications]);

  // *** NEW EFFECT FOR DELAYED UPDATE ***
  useEffect(() => {
    if (isDropdownOpen && hasUnread) {
      const timerId = setTimeout(() => {
        console.log('2 seconds passed. Marking notifications as read.');
        updateReadStatus();
      }, 2000); // 2-second delay

      return () => {
        clearTimeout(timerId); // Cleanup to prevent update if closed early
      };
    }
  }, [isDropdownOpen, hasUnread, updateReadStatus]);

  // *** MODIFIED CLICK HANDLER ***
  const handleBellClick = () => {
    // Always fetch fresh notifications on click
    fetchNotifications();
    setIsDropdownOpen((prev) => !prev);
  };

  const handleCloseDropdown = () => {
    setIsDropdownOpen(false);
  };

  return (
    <Flex
      direction="row"
      gap="2"
      justify={'between'}
      align={'center'}
      className='bg-[#F2F2F2] z-50 border-b-2 w-full min-h-[80px] sticky top-0'
      style={{ boxShadow: '0px 4px 28px rgba(0, 0, 0, 0.14)' }}
    >
      {/* ... Rest of your JSX is unchanged ... */}
      {!isSmallScreen && roleId !== '1' && (
        <Flex direction={'row'} className='py-2.5 px-2 space-x-4 ml-10 md:ml-2'>
          <Flex direction={'row'} align={'center'} className='space-x-3'>
            <img src={hospitalLogo} width={48} alt="Hospital" />
            <Text size={'4'}>{institutionName || 'Unknown Institution'}</Text>
          </Flex>
        </Flex>
      )}
      <Flex gap={'2'} className='py-2.5 px-4 ml-auto' align={'center'}>
        {!isSmallScreen && (
          <>
            {(roleId === '3' || roleId === '4') && (
              <Badge size="3" color="indigo">
                {department || 'Unknown Department'}
              </Badge>
            )}
          </>
        )}
        <Flex style={{ position: 'relative' }}>
          <IconButton
            size="4"
            variant="soft"
            onClick={handleBellClick}
            data-notification-bell="true"
          >
            <FaRegBell width="22" height="22" />
          </IconButton>
          {hasUnread && (
            <div
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: '#ff3b30',
                border: '2px solid #F2F2F2',
                boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.05)',
                animation: 'pulse 2s infinite',
              }}
            />
          )}
        </Flex>
        <Flex style={{ position: 'relative' }}>
          <ProfilePopup 
            triggerButton={
              <IconButton size="4" variant="soft">
                <FaRegUser width="22" height="22" />
              </IconButton>
            }
          />
          {roleId && ROLE_INDICATORS[roleId] && (
            <div
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: '#4b0082',
                border: '2px solid #F2F2F2',
                boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                lineHeight: '12px',
              }}
            >
              {ROLE_INDICATORS[roleId]}
            </div>
          )}
        </Flex>
        <IconButton onClick={() => setShowLogoutConfirm(true)} size="4" variant="soft">
          <FaPowerOff width="22" height="22" />
        </IconButton>
        {showLogoutConfirm && (
          <ConfirmationDialogue
            Type="Logout"
            Name="your session"
            Action="log out of"
            onCancel={() => setShowLogoutConfirm(false)}
            onConfirm={() => { setShowLogoutConfirm(false); logout(); }}
          />
        )}
      </Flex>
      <NotificationDropdown
        notifications={notifications}
        isOpen={isDropdownOpen}
        onClose={handleCloseDropdown}
        showGap={SHOW_DROPDOWN_GAP}
      />
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.7);
          }
          70% {
            transform: scale(1.1);
            box-shadow: 0 0 0 5px rgba(255, 59, 48, 0);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(255, 59, 48, 0);
          }
        }
      `}</style>
    </Flex>
  );
};

export default Header;